'use client';

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useMemo,
  useState
} from 'react';
import {
  ConsentConfigs,
  ConsentType,
  ConsentTypes,
  DefaultConsentVersion
} from './type';

export type ConsentContextType = {
  consentDetailOpen: boolean;
  setConsentDetailOpen: Dispatch<SetStateAction<boolean>>;
  selectedConsentType: ConsentType;
  setSelectedConsentType: Dispatch<SetStateAction<ConsentType>>;
  consentVersion: Record<string, string>;
  setConsentVersion: Dispatch<SetStateAction<Record<string, string>>>;
  detailContent: ReactNode;
  handleOpenDetailDialog: (consentType: ConsentType) => void;
  selectedProduct: string;
  setSelectedProduct: Dispatch<SetStateAction<string>>;
};

export const ConsentContext = createContext<ConsentContextType>({
  consentDetailOpen: false,
  setConsentDetailOpen: () => {},
  selectedConsentType: ConsentTypes.consent_personal_information,
  setSelectedConsentType: () => {},
  consentVersion: {},
  setConsentVersion: () => {},
  detailContent: <div />,
  handleOpenDetailDialog: () => {},
  selectedProduct: '',
  setSelectedProduct: () => {}
});

export const ConsentContextProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const [consentDetailOpen, setConsentDetailOpen] = useState<boolean>(false);
  const [selectedConsentType, setSelectedConsentType] = useState<ConsentType>(
    ConsentTypes.consent_personal_information
  );
  const [consentVersion, setConsentVersion] = useState<Record<string, string>>({
    ...DefaultConsentVersion
  });

  const handleOpenDetailDialog = (consentType: ConsentType) => {
    setSelectedConsentType(consentType);
    setConsentDetailOpen(true);
  };

  const [selectedProduct, setSelectedProduct] = useState<string>('H200');

  const detailContent = useMemo(() => {
    return (
      ConsentConfigs[selectedConsentType]?.config.find(
        (config) => config.version === consentVersion[selectedConsentType]
      )?.content || <div />
    );
  }, [selectedConsentType, consentVersion]);

  return (
    <ConsentContext.Provider
      value={{
        consentDetailOpen,
        setConsentDetailOpen,
        selectedConsentType,
        setSelectedConsentType,
        consentVersion,
        setConsentVersion,
        detailContent,
        handleOpenDetailDialog,
        selectedProduct,
        setSelectedProduct
      }}>
      {children}
    </ConsentContext.Provider>
  );
};
